/**
 * MVP restaurant menus: named PDF / image / URL documents per place.
 * Structured dishes (`place_menu_items`) stay Phase 2.
 */
import { DAY_KEYS, helsinkiNow, toMinutes, type DayKey } from "@/lib/opening-hours";
import { normalizeWebUrl } from "@/lib/normalize-url";
import type { PlaceMenu } from "@/lib/place-menu";

export type MenuSourceType = "pdf" | "image" | "url";

export type MenuAvailability = {
  mode: "always" | "schedule";
  days?: DayKey[];
  open?: string | null;
  close?: string | null;
};

export type PlaceMenuDoc = {
  id: string;
  placeId: string;
  name: string;
  sourceType: MenuSourceType;
  fileUrl: string | null;
  externalUrl: string | null;
  availability: MenuAvailability;
  priceNote: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export const EMPTY_AVAILABILITY: MenuAvailability = { mode: "always" };

export function parseAvailability(raw: unknown): MenuAvailability {
  if (!raw || typeof raw !== "object") return { ...EMPTY_AVAILABILITY };
  const o = raw as Record<string, unknown>;
  const days = Array.isArray(o.days)
    ? (o.days.filter((d) => typeof d === "string" && (DAY_KEYS as readonly string[]).includes(d)) as DayKey[])
    : undefined;
  const mode = o.mode === "schedule" ? "schedule" : "always";
  return {
    mode,
    days: days && days.length ? days : undefined,
    open: typeof o.open === "string" && o.open ? o.open : null,
    close: typeof o.close === "string" && o.close ? o.close : null,
  };
}

export function menuDocFromRow(row: Record<string, any>): PlaceMenuDoc {
  return {
    id: String(row.id),
    placeId: String(row.place_id),
    name: (row.name as string) || "Menu",
    sourceType: row.source_type === "pdf" || row.source_type === "image" ? row.source_type : "url",
    fileUrl: (row.file_url as string) || null,
    externalUrl: (row.external_url as string) || null,
    availability: parseAvailability(row.availability),
    priceNote: (row.price_note as string) || null,
    isActive: row.is_active !== false,
    sortOrder: Number(row.sort_order ?? 0),
    createdAt: String(row.created_at ?? ""),
    updatedAt: String(row.updated_at ?? ""),
  };
}

export function menuHref(doc: PlaceMenuDoc): string | null {
  if (doc.sourceType === "url") return doc.externalUrl || doc.fileUrl;
  return doc.fileUrl || doc.externalUrl;
}

export function menuIsImage(doc: PlaceMenuDoc): boolean {
  if (doc.sourceType === "image") return true;
  const href = menuHref(doc) ?? "";
  return /\.(avif|gif|jpe?g|png|webp)(\?|$)/i.test(href);
}

export function menuIsPdf(doc: PlaceMenuDoc): boolean {
  if (doc.sourceType === "pdf") return true;
  const href = menuHref(doc) ?? "";
  return /\.pdf(\?|$)/i.test(href);
}

/** True when this menu should be offered as currently available. Inactive menus never are. */
export function isMenuAvailableNow(doc: PlaceMenuDoc, now: Date = new Date()): boolean {
  if (!doc.isActive || !menuHref(doc)) return false;
  const a = doc.availability ?? EMPTY_AVAILABILITY;
  if (a.mode !== "schedule") return true;
  const { dayKey, minutes } = helsinkiNow(now);
  if (a.days && a.days.length > 0 && !a.days.includes(dayKey)) return false;
  const openM = toMinutes(a.open ?? null);
  let closeM = toMinutes(a.close ?? null);
  if (openM === null || closeM === null) return true;
  const overnight = closeM <= openM;
  if (overnight) closeM += 24 * 60;
  const nowM = minutes < openM && overnight && minutes + 24 * 60 < closeM ? minutes + 24 * 60 : minutes;
  return nowM >= openM && nowM < closeM;
}

export function availabilityLabel(doc: PlaceMenuDoc): string | null {
  const a = doc.availability;
  if (!a || a.mode !== "schedule") return null;
  const days =
    a.days && a.days.length && a.days.length < 7
      ? a.days.length === 5 && a.days.every((d) => !["Sat", "Sun"].includes(d))
        ? "Mon–Fri"
        : a.days.join(", ")
      : null;
  const time = a.open && a.close ? `${a.open.replace(/:00$/, "")}–${a.close.replace(/:00$/, "")}` : null;
  if (days && time) return `${days} ${time}`;
  return days || time;
}

/** Build document menus from the legacy three columns on `places`. */
export function menusFromLegacyPlace(place: {
  id?: string;
  menu_external_url?: string | null;
  menu_pdf_url?: string | null;
  menu_image_url?: string | null;
  menu_updated_at?: string | null;
}): PlaceMenuDoc[] {
  const placeId = String(place.id ?? "");
  const updated = place.menu_updated_at ?? "";
  const out: PlaceMenuDoc[] = [];
  let order = 0;
  const push = (name: string, sourceType: MenuSourceType, fileUrl: string | null, externalUrl: string | null) => {
    if (!fileUrl && !externalUrl) return;
    out.push({
      id: `legacy-${sourceType}`,
      placeId,
      name,
      sourceType,
      fileUrl,
      externalUrl,
      availability: { ...EMPTY_AVAILABILITY },
      priceNote: null,
      isActive: true,
      sortOrder: order++,
      createdAt: updated,
      updatedAt: updated,
    });
  };
  push("Menu PDF", "pdf", place.menu_pdf_url || null, null);
  push("Menu photo", "image", place.menu_image_url || null, null);
  push("Menu", "url", null, place.menu_external_url || null);
  if (out.length === 1) out[0]!.name = "Menu";
  return out;
}

/** Keep the three `places` columns in sync with the first active menu of each type. */
export function legacyColumnsFromMenus(menus: PlaceMenuDoc[]): {
  menu_external_url: string | null;
  menu_pdf_url: string | null;
  menu_image_url: string | null;
} {
  const active = menus.filter((m) => m.isActive && menuHref(m));
  const pdf = active.find((m) => m.sourceType === "pdf") ?? active.find(menuIsPdf);
  const image = active.find((m) => m.sourceType === "image") ?? active.find(menuIsImage);
  const url = active.find((m) => m.sourceType === "url") ?? active.find((m) => m.externalUrl);
  return {
    menu_pdf_url: normalizeWebUrl(pdf?.fileUrl ?? null),
    menu_image_url: normalizeWebUrl(image?.fileUrl ?? null),
    menu_external_url: normalizeWebUrl(url?.externalUrl ?? url?.fileUrl ?? null),
  };
}

export function docsToPlaceMenu(docs: PlaceMenuDoc[]): PlaceMenu {
  const active = docs.filter((m) => m.isActive);
  const pdf = active.find((m) => m.sourceType === "pdf");
  const image = active.find((m) => m.sourceType === "image");
  const url = active.find((m) => m.sourceType === "url");
  const any = active[0];
  return {
    url: url?.externalUrl ?? null,
    pdf: pdf?.fileUrl ?? null,
    image: image?.fileUrl ?? null,
    updatedAt: any?.updatedAt ?? null,
  };
}

export function docsFromUnknown(raw: unknown, placeId = ""): PlaceMenuDoc[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row, i) => {
      if (!row || typeof row !== "object") return null;
      const o = row as Record<string, any>;
      const name = String(o.name ?? "").trim();
      const fileUrl = (o.fileUrl ?? o.file_url ?? null) as string | null;
      const externalUrl = (o.externalUrl ?? o.external_url ?? null) as string | null;
      if (!name && !fileUrl && !externalUrl) return null;
      const sourceType: MenuSourceType =
        o.sourceType === "pdf" || o.source_type === "pdf"
          ? "pdf"
          : o.sourceType === "image" || o.source_type === "image"
            ? "image"
            : "url";
      return {
        id: String(o.id ?? `draft-${i}`),
        placeId,
        name: name || "Menu",
        sourceType,
        fileUrl,
        externalUrl,
        availability: parseAvailability(o.availability),
        priceNote: (o.priceNote ?? o.price_note ?? null) as string | null,
        isActive: o.isActive !== false && o.is_active !== false,
        sortOrder: Number(o.sortOrder ?? o.sort_order ?? i),
        createdAt: String(o.createdAt ?? o.created_at ?? ""),
        updatedAt: String(o.updatedAt ?? o.updated_at ?? ""),
      } satisfies PlaceMenuDoc;
    })
    .filter((d): d is PlaceMenuDoc => !!d);
}

export function detectSourceType(file: File): MenuSourceType {
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) return "pdf";
  return "image";
}

/** Public/loader helper: prefer `place_menus`, fall back to the three place columns. */
export async function loadPlaceMenuDocs(
  client: { from: (t: string) => any },
  place: {
    id: string;
    menu_external_url?: string | null;
    menu_pdf_url?: string | null;
    menu_image_url?: string | null;
    menu_updated_at?: string | null;
  },
  opts: { activeOnly?: boolean } = {},
): Promise<PlaceMenuDoc[]> {
  try {
    let q = client.from("place_menus").select("*").eq("place_id", place.id).order("sort_order", { ascending: true });
    if (opts.activeOnly !== false) q = q.eq("is_active", true);
    const { data, error } = await q;
    if (!error && Array.isArray(data) && data.length > 0) {
      return data.map((r: Record<string, any>) => menuDocFromRow(r));
    }
  } catch {
    /* table may not exist yet */
  }
  return menusFromLegacyPlace(place);
}
